import {useEffect} from 'react'
import {useAddBanner, useDismissBanner, type InitialBanner} from '@github-ui/banner-stack'
import {Link} from '@primer/react'

export const INVOCATION_BLOCKED_BANNER_ID = 'actions-invocation-blocked-banner'

const ACTIONS_SUPPORT_URL = 'https://support.github.com/contact?tags=dotcom-actions'

interface InvocationBlockedBannerOptions {
  repoBlocked: boolean
  userBlocked: boolean
}

type InvocationBlockedBanner = InitialBanner & {'data-test-selector'?: string}

/** Builds the highest-precedence invocation-blocked notice for BannerStack. */
export function getInvocationBlockedBanner({
  repoBlocked,
  userBlocked,
}: InvocationBlockedBannerOptions): InvocationBlockedBanner | undefined {
  if (!userBlocked && !repoBlocked) return undefined

  const blockedTarget = userBlocked ? 'your account' : 'this repository'

  return {
    id: INVOCATION_BLOCKED_BANNER_ID,
    title: `GitHub Actions is currently disabled for ${blockedTarget}.`,
    description: (
      <>
        Please reach out to{' '}
        <Link inline href={ACTIONS_SUPPORT_URL}>
          GitHub Support
        </Link>{' '}
        for assistance.
      </>
    ),
    variant: 'critical',
    'data-test-selector': userBlocked ? 'actions-invocation-blocked-user' : 'actions-invocation-blocked-repo',
  }
}

/** Keeps the invocation-blocked notice synchronized with the active workflow-runs route payload. */
export function useInvocationBlockedBanner({repoBlocked, userBlocked}: InvocationBlockedBannerOptions) {
  const addBanner = useAddBanner()
  const dismissBanner = useDismissBanner()

  useEffect(() => {
    const banner = getInvocationBlockedBanner({repoBlocked, userBlocked})
    if (!banner) {
      dismissBanner(INVOCATION_BLOCKED_BANNER_ID)
      return
    }

    const id = addBanner(banner)

    return () => {
      dismissBanner(id)
    }
  }, [repoBlocked, userBlocked, addBanner, dismissBanner])
}
